<template>
  <div
    v-if="timeRemains > 0 || timeToCalculate.type === 'catalog'"
    class="internet-counter text-lg-right"
    :class="{
      'finished p-0': finihedState,
      'incoming': auctionCatalogState === 'incoming'
    }"
  >
    <p
      class="mb-0 mr-2 mb-lg-1 artwork-countdown--header"
      v-if="timeToCalculate.type === 'artwork'"
    >
      {{ artworkHeader }}
    </p>
    <p
      class="m-0 mr-2 mr-lg-0"
      v-else-if="timeToCalculate.type === 'modal'"
    >
      {{ modalHeader }}
    </p>
    <p
      class="m-0 internet_auction-countdown--header"
      v-else
    >
      <span class="icon-auction mr-2" />
      {{ catalogHeader }}
    </p>
    <transition-group
      v-if="!finihedState"
      class="counter row no-gutters justify-content-lg-end align-items-center"
      :class="{
        'counter__catalog position-relative overflow-hidden': timeToCalculate.type === 'catalog',
        'counter__artwork': timeToCalculate.type === 'artwork',
      }"
      name="counter"
      tag="div"
    >
      <span
        v-for="dateUnit in allDatesUnits"
        v-if="dateUnit.show"
        :class="{
          'days col-12': dateUnit.unit === 'days',
          'text-center': dateUnit.unit === 'days' && timeToCalculate.type === 'catalog',
          'hours col-auto': dateUnit.unit !== 'days',
          'overtime': isOvertime,
        }"
        :key="dateUnit.alias"
      >
        {{ dateUnit.value | twoDigits }}
        <span
          :class="{ colon: dateUnit.unit === 'h' || dateUnit.unit === 'm' }"
        >
          {{ I18n.t(`internet_auctions.units.${dateUnit.unit}`) }}
        </span>
      </span>
    </transition-group>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import moment from 'moment';
import { AUCTIONS_CATALOG_STATES } from '@/shared/consts';

export default {
  name: 'InternetAuctionCountdown',
  props: {
    timeToCalculate: {
      type: Object,
    },
  },
  data() {
    return {
      currentTime: null,
      clientTime: null,
      second: 1000,
    };
  },
  mounted() {
    const newTime = (new Date()).getTime();
    const countdownDelay = newTime - this.serverTime;

    this.currentTime = this.serverTime + countdownDelay;
    this.clientTime = Date.parse(new Date());

    this.generateInterval = setInterval(() => {
      const newClientTime = Date.parse(new Date());
      const intervalDelay = newClientTime - this.clientTime;

      this.clientTime = newClientTime;
      this.currentTime += intervalDelay;
    }, this.second);
  },
  destroyed() {
    clearInterval(this.generateInterval);
  },
  methods: {
    ...mapMutations(['SET_CATALOG_STATE']),
  },
  watch: {
    auctionCatalogState(newState) {
      this.SET_CATALOG_STATE(newState);
    },
  },
  computed: {
    ...mapState(['serverTime']),
    allDatesUnits() {
      return [
        {
          alias: 'days',
          show: this.displayDays,
          unit: 'days',
          value: Math.floor(moment.duration(this.timeRemains).asDays()),
        },
        {
          alias: 'hours',
          show: this.displayHours,
          unit: 'h',
          value: moment.duration(this.timeRemains).hours(),
        },
        {
          alias: 'minutes',
          show: this.showMinutes,
          unit: 'm',
          value: moment.duration(this.timeRemains).minutes(),
        },
        {
          alias: 'seconds',
          show: this.showSeconds,
          unit: 's',
          value: moment.duration(this.timeRemains).seconds(),
        },
      ];
    },
    displayDays() {
      return moment.duration(this.timeRemains).asDays() > 1;
    },
    displayHours() {
      return !this.displayDays && moment.duration(this.timeRemains).asHours() > 1;
    },
    showMinutes() {
      return !this.displayDays && (
        moment.duration(this.timeRemains).asMinutes() > 1 ||
        moment.duration(this.timeRemains).asHours() > 1
      );
    },
    showSeconds() {
      return !this.displayDays && this.timeRemains >= 1;
    },
    timeRemains() {
      return this.timeInMilliseconds - this.currentTime;
    },
    auctionCatalogState() {
      if (this.timeToCalculate.type !== 'catalog') return true;
      if (this.startDateinMiliseconds >= this.currentTime) {
        return AUCTIONS_CATALOG_STATES.INCOMING;
      } else if (this.endDateinMiliseconds < this.currentTime) {
        return AUCTIONS_CATALOG_STATES.FINISHED;
      }
      return AUCTIONS_CATALOG_STATES.IN_PROGRESS;
    },
    timeInMilliseconds() {
      const isCatalogIncoming = this.auctionCatalogState === AUCTIONS_CATALOG_STATES.INCOMING;
      return isCatalogIncoming ? this.startDateinMiliseconds : this.endDateinMiliseconds;
    },
    startDateinMiliseconds() {
      return !!this.timeToCalculate.startDate && Date.parse(this.timeToCalculate.startDate);
    },
    endDateinMiliseconds() {
      return Date.parse(this.timeToCalculate.endDate);
    },
    catalogHeader() {
      switch (this.auctionCatalogState) {
        case AUCTIONS_CATALOG_STATES.INCOMING:
          return this.I18n.t('auctions_catalogs.starts_in');
        case AUCTIONS_CATALOG_STATES.IN_PROGRESS:
          return this.I18n.t('auctions_catalogs.final_in');
        case AUCTIONS_CATALOG_STATES.FINISHED:
          clearInterval(this.generateInterval);
          return this.I18n.t('auctions_catalogs.auction_final');
        default:
          return null;
      }
    },
    isOvertime() {
      return this.timeToCalculate.isOvertime;
    },
    artworkHeader() {
      if (this.isOvertime) {
        return this.I18n.t('internet_auctions.is_overtime');
      }
      return this.I18n.t('internet_auctions.end_countdown');
    },
    modalHeader() {
      if (this.isOvertime) {
        return this.I18n.t('internet_auctions.is_overtime');
      }
      return this.I18n.t('auctions_catalogs.final_in');
    },
    finihedState() {
      return this.timeToCalculate.type === 'catalog' &&
             this.allDatesUnits.every(element => element.show === false);
    },
  },
  filters: {
    twoDigits(value) {
      return (value.toString().length <= 1) ? `0${value}` : value;
    },
  },
};
</script>



// WEBPACK FOOTER //
// app/javascript/packs/internet_auctions/components/internet_auction_countdown.vue?620cd1f3